import type {
  Action as AgentAction,
  Agent,
  MatchConfig,
  Observation,
  SimEvent,
  Vec2,
} from "@cc/protocol";
import { TICKS_PER_SECOND } from "@cc/protocol";
import { Simulation, createDefaultConfig, derivePersonality } from "@cc/engine";
import { FALLBACK_PREFIX } from "@cc/agents";
import type { PersonalityCard, RenderState } from "@cc/engine";
import type { Locale } from "./i18n";
import { personaName } from "./i18n";
import { eventIcon } from "./theme";
import type { VisionMode, VisionView } from "./vision";
import { computeVision } from "./vision";

/* ------------------------------------------------------------------ */
/* View-facing types                                                   */
/* ------------------------------------------------------------------ */

/**
 * One row of the decision timeline.
 *
 * Deliberately *structured* rather than pre-rendered prose. Storing a finished
 * English sentence here made the language switch impossible: old rows would stay
 * English, and the only way out would be to restart the match. Keeping the raw
 * facts means `setLocale` can relabel the entire history instantly.
 */
export interface TimelineEntry {
  readonly id: number;
  readonly tick: number;
  readonly slot: number;
  readonly agentId: string;
  readonly kind: string;
  readonly icon: string;
  readonly action?: string;
  readonly reason?: string;
  readonly resourceKind?: string;
  readonly value?: number;
  readonly killedByTrap?: boolean;
  readonly isKey: boolean;
}

/** A post-match key moment, resolved to prose at render time. */
export interface MatchHighlight {
  readonly tick: number;
  readonly type: "trap" | "kill" | "interrupt" | "energy";
  readonly agentId?: string;
}

export interface Vfx {
  readonly id: number;
  readonly kind: "hit" | "collect" | "trap" | "spawn" | "scout" | "kill" | "miss" | "bomb" | "blade";
  readonly position: Vec2;
  readonly bornTick: number;
  readonly lifeTicks: number;
  readonly value?: number;
  readonly slot?: number;
}

export interface FloatingText {
  readonly id: number;
  readonly position: Vec2;
  readonly bornTick: number;
  readonly lifeTicks: number;
  /** Numbers are language-neutral; only labelled floats need a key. */
  readonly text: string;
  readonly key?: "channelLost" | "collectMissed";
  readonly color: string;
}

export interface AgentInfo {
  readonly slot: number;
  readonly id: string;
  readonly persona: string;
  readonly model: string;
  /** Ball icon (image URL or glyph). Optional for older call sites / bots. */
  readonly icon?: string;
}

export interface MatchOutcome {
  readonly winner: string | "draw";
  readonly winnerReason: "score" | "elimination" | "timeout" | "draw";
  readonly durationTicks: number;
  readonly scores: readonly { id: string; score: number; hp: number; totalScore: number }[];
  readonly highlights: readonly MatchHighlight[];
  readonly personalities: readonly PersonalityCard[];
}

const MAX_VFX = 70;
const MAX_TIMELINE = 400;

/**
 * How long the world will hold a tick waiting for a model decision.
 *
 * Generous on purpose: a reasoning model can legitimately think for many
 * wall-clock seconds, and abandoning early produces the "[fallback] skipped"
 * path that makes an AI cricket look broken. Finite so a wedged provider
 * cannot freeze the match permanently. When it trips, that tick is played as
 * WAIT and the match continues. Held time never consumes arena steps — that is
 * how a slow model stays fair against a fast one across the fixed 90-step match.
 */
const ASYNC_WAIT_CAP_MS = 60_000;

/**
 * Consecutive provider fallbacks before a model cricket is called degraded.
 *
 * Above one, because a single unparseable response is ordinary model behaviour
 * and flagging it would cry wolf. Low enough to be visible: at one decision per
 * second, this is a few seconds of a model that has effectively stopped playing.
 */
const DEGRADED_THRESHOLD = 3;

/**
 * The prefix `createLlmAgent` puts on every turn it failed to delegate to a
 * model. Imported from `@cc/agents` rather than re-spelled here, so the two
 * sides cannot drift apart.
 */

/**
 * Drives the simulation inside the browser at a real-time pace.
 *
 * Two clocks run side by side, exactly as the design docs require:
 *
 *  - The DOM simulation advances on a fixed 10 Hz tick grid, which is the world
 *    clock the agents perceive.
 *  - The canvas renders at up to 60 FPS, interpolating *between* the last two
 *    ticks using `alpha`, so motion looks smooth even though logic is coarse.
 *
 * Agents are asked for a decision only when their slot is due, so a model can
 * never be slowed down by the render rate, and a heavy frame can never change
 * the outcome of a match.
 */
export class MatchClient {
  readonly config: MatchConfig;
  readonly agents: readonly [Agent, Agent];
  readonly infos: readonly [AgentInfo, AgentInfo];

  timeline: TimelineEntry[] = [];
  vfx: Vfx[] = [];

  private sim: Simulation;
  private agentsRef: readonly [Agent, Agent];
  private accumulator = 0;
  private curr: RenderState;
  private prev: RenderState;
  private actionLog: { tick: number; agentId: string; slot: number; action: AgentAction }[] = [];
  private vfxSeq = 0;
  private tlSeq = 0;
  private eventCursor = 0;
  private listener: (() => void) | null = null;
  private outcome: MatchOutcome | null = null;
  private locale: Locale = "en";
  private asyncAgent = false;
  /**
   * In-flight model decisions, keyed `slot:tick`.
   *
   * Kept so a decision that arrives after its tick can be identified as stale
   * rather than applied late.
   */
  private pending = new Map<string, Promise<AgentAction>>();
  /** The most recent decision that has arrived but not yet been submitted. */
  private resolved = new Map<number, { tick: number; action: AgentAction }>();
  /** Ticks whose model request has exceeded the wait cap and been abandoned. */
  private timedOut = new Set<string>();

  /**
   * Per-slot provider-fallback tracking. See `noteDegradedTurn`.
   *
   * `consecutive` resets on any parsed model decision; `total` does not, because
   * the final report should say how unreliable the provider was overall, not
   * just whether it happened to fail at the end.
   */
  private degrade = new Map<number, { consecutive: number; total: number; announced: boolean }>();
  /** When each slot started waiting on the current tick's model request. */
  private waitingSince = new Map<number, number>();
  /**
   * Slots that have already submitted an action for the current sim tick.
   *
   * Required for aligned AI decisions: when both slots are due and A answers
   * before B, the world keeps holding. Without this set, every subsequent frame
   * would re-call `decide` on A (burning tokens and racing stale answers).
   * Cleared when the simulation finally steps.
   */
  private decidedThisTick = new Set<number>();
  /**
   * Per-cricket trap knowledge, tracked from `trap:detected` events.
   *
   * The engine's snapshot exposes a single global `revealed` flag for traps,
   * set when *either* cricket identifies one. The engine's own `knownTraps`
   * set is per-cricket, so the snapshot cannot be used to answer "can cricket A
   * see this trap?" without leaking B's knowledge into A's vision view. These
   * sets record the truth for the camera.
   */
  private knownTraps: [Set<string>, Set<string>] = [new Set(), new Set()];

  /** Timeline entries the viewer should be shown, newest last. */
  constructor(
    config: MatchConfig,
    agents: readonly [Agent, Agent],
    infos: readonly [AgentInfo, AgentInfo],
  ) {
    this.config = config;
    this.agents = agents;
    this.agentsRef = agents;
    this.infos = infos;
    this.sim = new Simulation(config);
    this.curr = this.sim.snapshot();
    this.prev = this.curr;
  }

  onChange(fn: () => void): void {
    this.listener = fn;
  }

  /**
   * Sets the display language.
   *
   * The match is not restarted and the timeline is not rebuilt: because entries
   * store structured facts rather than prose, every row simply re-renders in the
   * new language on the next React pass.
   */
  setLocale(locale: Locale): void {
    if (this.locale === locale) return;
    this.locale = locale;
    this.notify();
  }

  get currentLocale(): Locale {
    return this.locale;
  }

  private notify(): void {
    this.listener?.();
  }

  get finished(): boolean {
    return this.sim.finished;
  }

  /**
   * Runs the match to completion as fast as the CPU allows.
   *
   * Used by the replay page so a viewer can scrub a finished match immediately
   * instead of watching five minutes play out. A replay must not re-record
   * anything (it is already a replay), so this marks the client ephemeral.
   */
  fastForward(maxTicks = 100_000): void {
    let guard = 0;
    while (!this.sim.finished && this.sim.tick < maxTicks) {
      this.stepOnce();
      if (++guard > maxTicks) break;
    }
    if (!this.sim.finished) this.sim.forceFinish("timeout");
    this.processEvents();
    this.finalise();
    this.prev = this.curr;
    this.curr = this.sim.snapshot();
    this.notify();
  }

  get tick(): number {
    return this.sim.tick;
  }

  get timeRemainingTicks(): number {
    return this.sim.timeRemainingTicks;
  }

  /** Latest completed tick. */
  get current(): RenderState {
    return this.curr;
  }

  /** Tick before `current`, for interpolation. */
  get previous(): RenderState {
    return this.prev;
  }

  get progress(): number {
    return Math.min(1, this.sim.tick / this.config.durationTicks);
  }

  getResult(): MatchOutcome | null {
    return this.outcome;
  }

  /** Raw behavioural counters for a cricket, used by the result cards. */
  behaviouralOf(agentId: string) {
    const found = this.sim.getCricketResults().find((r) => r.id === agentId);
    return found ? found.behavioural : null;
  }

  /**
   * True when at least one cricket is a real model that thinks asynchronously.
   *
   * Surfaced so the UI can warn that a match with a live model is not
   * bit-reproducible from the seed alone. That caveat is inherent, not a bug:
   * a network round trip is wall-clock time, and the engine's determinism
   * contract assumes decisions arrive on the tick grid.
   */
  get hasAsyncAgent(): boolean {
    return this.asyncAgent;
  }

  /**
   * Slots currently held waiting for a live-model decision.
   *
   * Surfaced in the HUD as a "thinking" badge so a multi-second pause reads as
   * the model working, not as a frozen arena.
   */
  waitingSlots(): readonly number[] {
    if (!this.asyncAgent) return [];
    const out: number[] = [];
    for (const slot of [0, 1] as const) {
      if (!this.sim.isDecisionDue(slot)) continue;
      if (this.decidedThisTick.has(slot)) continue;
      const key = `${slot}:${this.sim.tick}`;
      if (this.pending.has(key) && !this.timedOut.has(key)) out.push(slot);
    }
    return out;
  }

  /**
   * Advances the world by a wall-clock delta.
   *
   * `speed` is a playback multiplier: 1 is real time, 4 is four times faster.
   * The tick grid is preserved at every speed, so speeding up never changes
   * what happens — it only changes how many ticks we consume per frame.
   */
  advance(deltaMs: number, speed: number): void {
    if (this.sim.finished) return;

    const ticksPerMs = TICKS_PER_SECOND / 1000;
    this.accumulator += deltaMs * speed * ticksPerMs;

    // Cap the catch-up so a backgrounded tab does not fast-forward the match.
    const maxTicksPerFrame = TICKS_PER_SECOND * 3;
    let stepped = 0;
    while (this.accumulator >= 1 && stepped < maxTicksPerFrame && !this.sim.finished) {
      this.accumulator -= 1;
      // A false result means the world is holding a tick for a model decision.
      // Stop, and hand the unused budget back so we do not build up a debt that
      // would later fast-forward a burst of ticks.
      if (!this.stepOnce()) {
        this.accumulator = 0;
        break;
      }
      stepped++;
    }
    if (stepped >= maxTicksPerFrame) this.accumulator = 0;

    if (stepped > 0) {
      this.processEvents();
      this.notify();
    }
  }

  /**
   * One simulation tick.
   *
   * Returns false when the world could not advance because a model decision is
   * still in flight, so the caller stops consuming its time budget.
   *
   * The synchronous path is unchanged: poll, submit, step. The asynchronous path
   * exists because `Agent.decide` returns a promise for real models, while
   * `step()` is synchronous. The headless driver resolves this by *awaiting* the
   * decision before stepping, so the action is applied on the same tick the
   * observation was taken from. We do the same thing, holding the tick instead
   * of the thread:
   *
   *   - hold the world while a decision is outstanding, so the model acts on the
   *     state it actually reasoned about (letting the world run would apply a
   *     stale decision to a board it never saw);
   *   - never re-poll the same tick, which would fire a second request;
   *   - give up after `ASYNC_WAIT_CAP_MS` so a wedged provider cannot freeze the
   *     match forever.
   */
  private stepOnce(): boolean {
    for (const slot of [0, 1] as const) {
      if (!this.sim.isDecisionDue(slot)) continue;
      if (this.decidedThisTick.has(slot)) continue;
      const key = `${slot}:${this.sim.tick}`;

      // The model has answered for this tick: apply it now.
      const done = this.resolved.get(slot);
      if (done && done.tick === this.sim.tick) {
        this.resolved.delete(slot);
        this.pending.delete(key);
        this.waitingSince.delete(slot);
        this.commitAction(slot, done.action);
        continue;
      }

      // Still waiting on this tick's request, or it already timed out.
      if (this.pending.has(key) || this.timedOut.has(key)) continue;

      const agent = this.agentsRef[slot]!;
      let action: ReturnType<Agent["decide"]>;
      try {
        action = agent.decide(this.sim.observe(slot));
      } catch {
        action = { action: "WAIT" };
      }

      if (action && typeof (action as Promise<unknown>).then === "function") {
        this.asyncAgent = true;
        this.trackPending(slot, this.sim.tick, action as Promise<AgentAction>);
        continue;
      }

      this.commitAction(slot, action as AgentAction);
    }

    if (this.awaitingModel()) return false;

    this.prev = this.curr;
    this.sim.step();
    this.decidedThisTick.clear();
    this.curr = this.sim.snapshot();
    return true;
  }

  /** True when some slot still owes a decision for the current tick. */
  private awaitingModel(): boolean {
    if (!this.asyncAgent) return false;
    const now = Date.now();
    for (const slot of [0, 1] as const) {
      if (!this.sim.isDecisionDue(slot)) continue;
      if (this.decidedThisTick.has(slot)) continue;
      const key = `${slot}:${this.sim.tick}`;
      if (!this.pending.has(key) || this.timedOut.has(key)) continue;

      const since = this.waitingSince.get(slot);
      if (since === undefined) {
        // First frame of this wait: start the clock and keep holding. Returning
        // "not waiting" here would let the world advance one tick and the model
        // would then be answering a board that has already moved on.
        this.waitingSince.set(slot, now);
        return true;
      }
      if (now - since > ASYNC_WAIT_CAP_MS) {
        // Safety valve: stop holding forever, but still record a decision so
        // this tick is not silently skipped — and so we never re-poll the slot.
        this.timedOut.add(key);
        this.pending.delete(key);
        this.waitingSince.delete(slot);
        this.resolved.delete(slot);
        this.commitAction(slot, {
          action: "WAIT",
          reason: "[fallback] decision wait timed out",
        });
        continue;
      }
      return true;
    }
    return false;
  }

  /** Records an action on the replay log, submits it and samples behaviour. */
  private commitAction(slot: number, action: AgentAction): void {
    this.decidedThisTick.add(slot);
    this.sim.submit(slot, action);
    this.actionLog.push({ tick: this.sim.tick, agentId: this.infos[slot]!.id, slot, action });
    this.sim.sampleBehaviour(slot);
    this.noteDegradedTurn(slot, action);
  }

  /**
   * Notices a model that has stopped being a model.
   *
   * `createLlmAgent` handles provider failures gracefully by falling back to a
   * heuristic for that turn and recording `reason: "[fallback] …"` on the action.
   * That is the right call for keeping a match alive — but the *reason is then
   * overwritten by the engine's own `agent:decision` event*, which carries the
   * heuristic's phrasing. The result is a match labelled "BROKEN BOT · a live
   * model" in which a plain bot is playing and nothing on screen says so.
   *
   * That is the exact failure the rest of this codebase refuses to commit: an AI
   * match must never silently be a bot match. So it is detected here, in the
   * client, at the one point where the agent's own reason is still visible.
   *
   * A single fallback is normal and not worth alarming anyone about — models
   * emit unparseable output occasionally. It becomes a *story* about the match
   * only once it is sustained, which is when the cricket has effectively become
   * a bot.
   */
  private noteDegradedTurn(slot: number, action: AgentAction): void {
    const id = this.infos[slot]!.id;
    if (this.infos[slot]!.model === "heuristic") return;

    if (typeof action.reason === "string" && action.reason.startsWith(FALLBACK_PREFIX)) {
      const state = this.degrade.get(slot);
      if (!state) this.degrade.set(slot, { consecutive: 1, announced: false, total: 1 });
      else {
        state.consecutive++;
        state.total++;
      }
      return;
    }
    // A parsed model decision: the provider is healthy again.
    const state = this.degrade.get(slot);
    if (state) {
      state.consecutive = 0;
      state.announced = false;
    }
  }

  /**
   * A model that has fallen back to its heuristic often enough to be described
   * as degraded. Surfaced in the UI so the label "AI" on a spinning bot is never
   * a lie, and so the viewer can tell "the model is bad at this" apart from
   * "your key stopped working".
   */
  degradedSlots(): readonly number[] {
    const out: number[] = [];
    for (const [slot, state] of this.degrade) {
      if (state.consecutive >= DEGRADED_THRESHOLD) out.push(slot);
    }
    return out;
  }

  /** Total per-turn provider fallbacks observed, for the result card. */
  fallbackCount(slot: number): number {
    return this.degrade.get(slot)?.total ?? 0;
  }

  /**
   * Caches an in-flight model decision.
   *
   * The promise never rejects in practice: `createLlmAgent` converts every
   * failure into a heuristic action. A rejection here would be a programming
   * error, and degrading it to WAIT keeps the match alive rather than throwing
   * inside the render loop.
   */
  private trackPending(slot: number, tick: number, promise: Promise<AgentAction>): void {
    const key = `${slot}:${tick}`;
    this.pending.set(key, promise);
    promise
      .then((action) => {
        // Timed-out or already-committed ticks must not resurrect a late answer.
        if (this.timedOut.has(key) || this.decidedThisTick.has(slot)) return;
        this.resolved.set(slot, { tick, action });
      })
      .catch(() => {
        if (this.timedOut.has(key) || this.decidedThisTick.has(slot)) return;
        this.resolved.set(slot, { tick, action: { action: "WAIT" } });
      });
  }

  /** Converts raw engine events into timeline rows, VFX and floating numbers. */
  private processEvents(): void {
    const events = this.sim.events;
    for (let i = this.eventCursor; i < events.length; i++) {
      const ev = events[i]!;
      this.handleEvent(ev);
    }
    this.eventCursor = events.length;

    // Expire effects. Done here rather than on a timer so VFX are tied to the
    // world clock and stay in sync when playback speed changes.
    const cutoff = this.sim.tick - 30;
    if (this.vfx.length > 0) this.vfx = this.vfx.filter((v) => v.bornTick > cutoff);
    if (this.vfx.length > MAX_VFX) this.vfx = this.vfx.slice(-MAX_VFX);
    if (this.timeline.length > MAX_TIMELINE) {
      this.timeline = this.timeline.slice(-MAX_TIMELINE);
    }
  }

  private handleEvent(ev: SimEvent): void {
    const slot = ev.agentId ? this.slotOf(ev.agentId) : -1;

    switch (ev.type) {
      case "agent:decision": {
        const action = String(ev.data.action ?? "WAIT");
        const reason = typeof ev.data.reason === "string" ? ev.data.reason : undefined;
        this.pushTimeline({
          tick: ev.tick,
          slot,
          agentId: ev.agentId ?? "",
          kind: "decision",
          action,
          ...(reason === undefined ? {} : { reason }),
          isKey: false,
        });
        break;
      }
      case "agent:collect:complete": {
        const pos = asVec2(ev.data.position) ?? this.positionOf(ev.agentId);
        const value = Number(ev.data.value ?? 0);
        const kind = String(ev.data.kind ?? "crystal");
        this.pushVfx("collect", pos, ev.tick, 16, value, slot);
        this.pushFloat(pos, `+${value}`, "#22d3ee", ev.tick);
        this.pushTimeline({
          tick: ev.tick,
          slot,
          agentId: ev.agentId ?? "",
          kind: "collect",
          resourceKind: kind,
          value,
          isKey: kind === "energy",
        });
        break;
      }
      case "trap:triggered": {
        const pos = asVec2(ev.data.position) ?? this.positionOf(ev.agentId);
        const damage = Number(ev.data.damage ?? 0);
        this.pushVfx("trap", pos, ev.tick, 34, damage, slot);
        this.pushFloat(pos, `-${damage} HP`, "#ef4444", ev.tick);
        this.pushTimeline({
          tick: ev.tick,
          slot,
          agentId: ev.agentId ?? "",
          kind: "trap",
          isKey: true,
        });
        break;
      }
      case "trap:detected": {
        const pos = this.positionOf(ev.agentId);
        // Record *whose* knowledge this is. See the `knownTraps` field: the
        // snapshot's flag is global and would leak this across crickets.
        const resourceId = String(ev.data.resourceId ?? "");
        if (resourceId && slot >= 0) this.knownTraps[slot as 0 | 1].add(resourceId);
        this.pushVfx("scout", pos, ev.tick, 20, undefined, slot);
        this.pushTimeline({
          tick: ev.tick,
          slot,
          agentId: ev.agentId ?? "",
          kind: "detect",
          isKey: false,
        });
        break;
      }
      case "agent:attack": {
        const pos = asVec2(ev.data.position) ?? this.positionOf(ev.agentId);
        const damage = Number(ev.data.damage ?? 0);
        const defending = ev.data.defending === true;
        this.pushVfx("hit", pos, ev.tick, 12, damage, slot);
        if (damage > 0) this.pushFloat(pos, `-${damage}`, defending ? "#fbbf24" : "#ff2d55", ev.tick);
        break;
      }
      case "agent:hit": {
        // Rendered through agent:attack so damage is not counted twice.
        break;
      }
      case "agent:attack:miss": {
        const pos = this.positionOf(ev.agentId);
        this.pushVfx("miss", pos, ev.tick, 10, undefined, slot);
        break;
      }
      case "agent:collect:interrupted": {
        const pos = asVec2(ev.data.position) ?? this.positionOf(ev.agentId);
        this.pushTimeline({
          tick: ev.tick,
          slot,
          agentId: ev.agentId ?? "",
          kind: "interrupt",
          isKey: true,
        });
        this.pushFloat(pos, "", "#fbbf24", ev.tick, "channelLost");
        break;
      }
      case "agent:collect:miss": {
        const pos = this.positionOf(ev.agentId);
        const reason = String(ev.data.reason ?? "failed");
        this.pushVfx("miss", pos, ev.tick, 10, undefined, slot);
        this.pushTimeline({
          tick: ev.tick,
          slot,
          agentId: ev.agentId ?? "",
          kind: "collectMiss",
          reason,
          isKey: false,
        });
        this.pushFloat(pos, "", "#fbbf24", ev.tick, "collectMissed");
        break;
      }
      case "agent:eliminated": {
        const pos = this.positionOf(ev.agentId);
        const by = String(ev.data.by ?? "");
        this.pushVfx("kill", pos, ev.tick, 60, undefined, slot);
        this.pushTimeline({
          tick: ev.tick,
          slot,
          agentId: ev.agentId ?? "",
          kind: "kill",
          killedByTrap: by === "trap",
          isKey: true,
        });
        break;
      }
      case "agent:scouted": {
        const center = asVec2(ev.data.center) ?? this.positionOf(ev.agentId);
        this.pushVfx("scout", center, ev.tick, 22, Number(ev.data.resourcesFound ?? 0), slot);
        break;
      }
      case "agent:flee": {
        this.pushTimeline({
          tick: ev.tick,
          slot,
          agentId: ev.agentId ?? "",
          kind: "flee",
          action: "FLEE",
          isKey: false,
        });
        break;
      }
      case "agent:exchange": {
        if (ev.data.ok !== true) break;
        this.pushTimeline({
          tick: ev.tick,
          slot,
          agentId: ev.agentId ?? "",
          kind: "exchange",
          action: "EXCHANGE",
          resourceKind: String(ev.data.item ?? ""),
          value: Number(ev.data.cost ?? 0),
          isKey: false,
        });
        break;
      }
      case "agent:use": {
        if (ev.data.ok !== true) break;
        this.pushTimeline({
          tick: ev.tick,
          slot,
          agentId: ev.agentId ?? "",
          kind: "use",
          action: "USE",
          resourceKind: String(ev.data.item ?? ""),
          isKey: true,
        });
        break;
      }
      case "item:bomb": {
        const pos = asVec2(ev.data.position) ?? this.positionOf(ev.agentId);
        const damage = Number(ev.data.damage ?? 0);
        this.pushVfx("bomb", pos, ev.tick, 28, damage, slot);
        this.pushFloat(pos, `BOMB −${damage}`, "#f97316", ev.tick);
        this.pushTimeline({
          tick: ev.tick,
          slot,
          agentId: ev.agentId ?? "",
          kind: "bomb",
          value: damage,
          isKey: true,
        });
        break;
      }
      case "item:blade": {
        const pos = asVec2(ev.data.position) ?? this.positionOf(ev.agentId);
        this.pushVfx("blade", pos, ev.tick, 24, undefined, slot);
        this.pushTimeline({
          tick: ev.tick,
          slot,
          agentId: ev.agentId ?? "",
          kind: "blade",
          isKey: true,
        });
        break;
      }
      case "resource:spawn": {
        const pos = asVec2(ev.data.position);
        if (pos) this.pushVfx("spawn", pos, ev.tick, 24, undefined, -1);
        break;
      }
      case "match:end": {
        this.finalise();
        break;
      }
      default:
        break;
    }
  }

  private finalise(): void {
    if (this.outcome) return;
    const results = this.sim.getCricketResults();
    this.outcome = {
      winner: this.sim.getWinner(),
      winnerReason: this.sim.getWinnerReason(),
      durationTicks: this.sim.tick,
      scores: results.map((r) => ({
        id: r.id,
        score: r.score,
        hp: r.hp,
        totalScore: r.totalScore,
      })),
      highlights: scoreTopHighlights(this.sim.events, this.locale),
      personalities: results.map((r) =>
        derivePersonality(r, this.config.durationTicks, this.config.arenaSize),
      ),
    };
    this.notify();
  }

  /* ---------------------------------------------------------------- */
  /* Replay-style seeking                                             */
  /* ---------------------------------------------------------------- */

  /**
   * Rewinds (or fast-forwards through recorded history) to a given tick.
   *
   * Because the engine is deterministic, a seek is just "re-simulate from tick
   * zero and re-submit the actions we already recorded". That is the same
   * mechanism the headless replay uses, which is why scrubbing the timeline is
   * cheap and always self-consistent.
   *
   * One caveat worth knowing: a stateful LLM agent cannot be rewound, so after
   * a seek it continues with whatever memory it has accumulated. For the
   * deterministic heuristic crickets a seek is completely seamless.
   */
  seekTo(targetTick: number): void {
    const target = Math.max(0, Math.min(this.actionLog.length ? this.sim.tick : 0, targetTick));
    if (target > this.sim.tick) return; // History is authoritative; no future to fast-forward into.

    const sim = new Simulation(this.config);
    this.agentsRef = this.agents;
    const grouped = new Map<number, { slot: number; action: unknown }[]>();
    for (const entry of this.actionLog) {
      if (entry.tick > target) break;
      const list = grouped.get(entry.tick);
      if (list) list.push({ slot: entry.slot, action: entry.action });
      else grouped.set(entry.tick, [{ slot: entry.slot, action: entry.action }]);
    }

    while (sim.tick < target && !sim.finished) {
      const pending = grouped.get(sim.tick);
      if (pending) {
        for (const p of pending) sim.submit(p.slot, p.action as never);
      }
      sim.step();
    }

    this.sim = sim;
    this.curr = sim.snapshot();
    this.prev = this.curr;
    this.eventCursor = sim.events.length;
    this.accumulator = 0;
    this.timeline = [];
    this.vfx = [];
    this.outcome = null;
    // Rebuild trap knowledge from the reconstructed event history, otherwise a
    // rewind would leave crickets knowing about traps they have not found yet.
    this.knownTraps = [new Set(), new Set()];
    this.resolved.clear();
    this.pending.clear();
    // Recompute fallback counts from the recorded actions rather than clearing
    // them: a rewind replays the same judgements, so the degraded verdict must
    // survive a seek or scrubbing would "heal" a broken provider.
    this.degrade.clear();
    for (const entry of this.actionLog) {
      if (entry.tick <= target) this.noteDegradedTurn(entry.slot, entry.action);
    }
    for (const ev of sim.events) this.handleEvent(ev);
    this.notify();
  }

  get canSeek(): boolean {
    return this.actionLog.length > 0;
  }

  /**
   * The recorded action log, in submission order.
   *
   * Exposed because it is the match's durable artefact: replay reconstruction,
   * scrubbing and verification all rebuild the world from exactly this list.
   */
  fullLog(): readonly { readonly tick: number; readonly agentId: string; readonly slot: number; readonly action: AgentAction }[] {
    return this.actionLog;
  }

  /**
   * The information set to draw for a given vision mode.
   *
   * Spectator mode returns null, meaning "show everything". Any other mode
   * masks the arena down to what that one cricket can currently see.
   */
  visionFor(mode: VisionMode): VisionView | null {
    // Refresh event-derived state first. `processEvents` is idempotent (guarded
    // by an event cursor), and doing it here makes this accessor correct
    // regardless of call order — otherwise the camera could lag one frame behind
    // on trap knowledge, which is exactly the information the view exists to get
    // right.
    this.processEvents();
    return computeVision(
      this.curr,
      mode,
      this.config.stats.visionRadius,
      // Trap knowledge is per-cricket and tracked from events, not read off the
      // snapshot's global `revealed` flag.
      mode === "spectator" ? () => true : (id) => this.knownTraps[mode].has(id),
    );
  }

  /**
   * Reconstructs what a cricket could see at an arbitrary past tick.
   *
   * Used by the "Why?" inspector. We rebuild a throwaway simulation and replay
   * the recorded actions up to the requested tick, which is only possible
   * because the engine is deterministic. For ticks the match has not reached
   * yet, this returns null rather than guessing.
   */
  observationAt(targetTick: number, slot: number): Observation | null {
    if (targetTick > this.sim.tick) return null;
    if (targetTick === this.sim.tick) return this.sim.observe(slot);

    const sim = new Simulation(this.config);
    const grouped = new Map<number, { slot: number; action: unknown }[]>();
    for (const entry of this.actionLog) {
      if (entry.tick > targetTick) break;
      const list = grouped.get(entry.tick);
      if (list) list.push({ slot: entry.slot, action: entry.action });
      else grouped.set(entry.tick, [{ slot: entry.slot, action: entry.action }]);
    }

    while (sim.tick < targetTick && !sim.finished) {
      const pending = grouped.get(sim.tick);
      if (pending) {
        for (const p of pending) sim.submit(p.slot, p.action as never);
      }
      sim.step();
    }
    return sim.observe(slot);
  }

  /* ---------------------------------------------------------------- */
  /* Helpers                                                           */
  /* ---------------------------------------------------------------- */

  private pushTimeline(entry: Omit<TimelineEntry, "id" | "icon">): void {
    this.timeline.push({
      ...entry,
      id: this.tlSeq++,
      icon: eventIcon(entry.kind),
    });
  }

  private pushVfx(
    kind: Vfx["kind"],
    position: Vec2,
    tick: number,
    lifeTicks: number,
    value?: number,
    slot?: number,
  ): void {
    this.vfx.push({
      id: this.vfxSeq++,
      kind,
      position,
      bornTick: tick,
      lifeTicks,
      ...(value === undefined ? {} : { value }),
      ...(slot === undefined ? {} : { slot }),
    });
  }

  private floatSeq = 0;
  readonly floats: FloatingText[] = [];

  private pushFloat(
    position: Vec2,
    text: string,
    color: string,
    tick: number,
    key?: FloatingText["key"],
  ): void {
    this.floats.push({
      id: this.floatSeq++,
      position,
      bornTick: tick,
      lifeTicks: 28,
      text,
      ...(key === undefined ? {} : { key }),
      color,
    });
    if (this.floats.length > 40) this.floats.splice(0, this.floats.length - 40);
  }

  syncFloats(): void {
    const cutoff = this.sim.tick - 28;
    for (let i = this.floats.length - 1; i >= 0; i--) {
      if (this.floats[i]!.bornTick <= cutoff) this.floats.splice(i, 1);
    }
  }

  slotOf(agentId: string): number {
    return this.infos[0].id === agentId ? 0 : 1;
  }

  /** Raw persona id. Translation is the caller's job. */
  personaOf(agentId: string): string {
    return this.infos[0].id === agentId ? this.infos[0].persona : this.infos[1].persona;
  }

  /** Persona already translated for the current locale. */
  displayName(agentId: string): string {
    return personaName(this.personaOf(agentId), this.locale);
  }

  private positionOf(agentId: string | undefined): Vec2 {
    if (!agentId) return [0, 0];
    const cricket = this.curr.crickets.find((c) => c.id === agentId);
    return cricket ? cricket.position : [0, 0];
  }
}

function asVec2(value: unknown): Vec2 | null {
  if (Array.isArray(value) && value.length >= 2) {
    const x = Number(value[0]);
    const y = Number(value[1]);
    if (Number.isFinite(x) && Number.isFinite(y)) return [x, y];
  }
  return null;
}

/**
 * Picks the most interesting moments of a match.
 *
 * Returns *structured* highlights. Naming who did what is deferred to the render
 * layer, otherwise a real model's persona would be baked into the result in
 * whatever language happened to be active when the match ended.
 */
function scoreTopHighlights(
  events: readonly SimEvent[],
  _locale: Locale,
): MatchHighlight[] {
  const out: { tick: number; type: MatchHighlight["type"]; agentId?: string; weight: number }[] = [];
  for (const ev of events) {
    if (ev.type === "trap:triggered") {
      out.push({
        tick: ev.tick,
        type: "trap",
        weight: 90,
        ...(ev.agentId ? { agentId: ev.agentId } : {}),
      });
    } else if (ev.type === "agent:eliminated") {
      out.push({
        tick: ev.tick,
        type: "kill",
        weight: 95,
        ...(ev.agentId ? { agentId: ev.agentId } : {}),
      });
    } else if (ev.type === "agent:collect:interrupted") {
      out.push({
        tick: ev.tick,
        type: "interrupt",
        weight: 46,
        ...(ev.agentId ? { agentId: ev.agentId } : {}),
      });
    } else if (ev.type === "agent:collect:complete" && ev.data.kind === "energy") {
      out.push({
        tick: ev.tick,
        type: "energy",
        weight: 50,
        ...(ev.agentId ? { agentId: ev.agentId } : {}),
      });
    }
  }

  // One moment per (type, agent): six trap hits in a row is one story, not six.
  const seen = new Set<string>();
  return out
    .sort((a, b) => b.weight - a.weight)
    .filter((h) => {
      const key = `${h.type}:${h.agentId ?? ""}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice(0, 6)
    .sort((a, b) => a.tick - b.tick);
}

export { createDefaultConfig };
